// Records a consented attribute disclosure for a survey and triggers the data
// reward (reputation + inventory) via a tamper-resistant RPC.

import { supabase } from './supabaseClient';
import { AttributeValue } from './zkVault/types';

export interface AttributeDisclosure {
  id: string;
  survey_id: string;
  response_id: string;
  user_id: string;
  disclosed: Record<string, AttributeValue>;
  created_at: string;
}

export const shareAttributes = async (
  surveyId: string,
  responseId: string,
  disclosed: Record<string, AttributeValue>,
): Promise<number> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('You must be signed in to share data.');

  const { error } = await supabase.from('survey_attribute_disclosures').insert({
    survey_id: surveyId,
    response_id: responseId,
    user_id: user.id,
    disclosed,
  });
  if (error) throw error;

  // Reward (reputation + inventory). Idempotent server-side.
  const { data, error: awardErr } = await supabase.rpc('award_data_share', {
    p_survey_id: surveyId,
    p_response_id: responseId,
  });
  if (awardErr) {
    console.error('Data-share reward failed:', awardErr);
    return 0;
  }
  return (data as number) ?? 0;
};

// Researcher-facing (RLS lets the survey creator read disclosures for their survey).
export const fetchDisclosureForResponse = async (responseId: string): Promise<AttributeDisclosure | null> => {
  const { data, error } = await supabase
    .from('survey_attribute_disclosures')
    .select('*')
    .eq('response_id', responseId)
    .maybeSingle();
  if (error) {
    console.error('Failed to fetch disclosure:', error);
    return null;
  }
  return (data as AttributeDisclosure) || null;
};
